import React from "react";
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  useTheme,
} from "@mui/material";
import { Close as CloseIcon } from "@mui/icons-material";
import CustomTypography from "../form/CustomTypography";

interface Pack {
  name: string;
  size: string;
  color: string;
  quantity: string;
}

interface ViewPackProductsPopupProps {
  open: boolean;
  packProductsLabel: string | null | undefined;
  onClose: () => void;
}

const ViewPackProductsPopup: React.FC<ViewPackProductsPopupProps> = ({
  open,
  packProductsLabel,
  onClose,
}) => {
  const theme = useTheme();

  // Parse packs_label string into Pack objects
  const parsePacks = (label: string | null | undefined): Pack[] => {
    if (!label || label.trim() === "" || label === "-") {
      return [];
    }

    return label
      .split(",")
      .map((packStr) => packStr.trim())
      .filter((packStr) => packStr.length > 0)
      .map((packStr) => {
        const parts = packStr.split("/");
        return {
          name: parts[0]?.trim() || "-",
          size: parts[1]?.trim() || "-",
          color: parts[2]?.trim() || "-",
          quantity: parts[3]?.trim() || "-",
        };
      });
  };

  const packs = parsePacks(packProductsLabel);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      slotProps={{
        transition: {
          timeout: 300,
          sx: {
            backgroundColor: theme.palette.customColors.backdrop,
            backdropFilter: "blur(4px)",
          },
        },
        backdrop: {
          timeout: 300,
          sx: {
            backgroundColor: theme.palette.customColors.backdrop,
            backdropFilter: "blur(4px)",
          },
        },
        paper: {
          sx: {
            borderRadius: "16px",
            boxShadow: "0 20px 40px rgba(0, 0, 0, 0.15)",
            overflow: "hidden",
            maxHeight: "90vh",
            maxWidth: {
              xs: "95vw",
              sm: "90vw",
              md: "70vw",
              lg: "60vw",
              xl: "50vw",
            },
            width: {
              xs: "95vw",
              sm: "90vw",
              md: "70vw",
              lg: "60vw",
              xl: "50vw",
            },
            margin: { xs: "16px", sm: "32px" },
            background: `linear-gradient(135deg, ${theme.palette.customColors.white} 0%, ${theme.palette.customColors.lightGreyColor} 100%)`,
          },
        },
      }}
      sx={{
        zIndex: 2000,
        "& .MuiDialog-container": {
          alignItems: "center",
          justifyContent: "center",
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          height: "100%",
          position: "relative",
          "&::before": {
            content: '""',
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            height: "4px",
            background: `linear-gradient(90deg, ${theme.palette.customColors.blueGradientStart} 0%, ${theme.palette.customColors.blueGradientEnd} 50%, ${theme.palette.customColors.blueGradientStart} 100%)`,
          },
        }}
      >
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: { xs: "12px 16px 12px", sm: "16px 20px 16px" },
            borderBottom: `1px solid ${theme.palette.customColors.greyBorder}`,
          }}
        >
          <CustomTypography variant="heading3">
            View Pack Products
          </CustomTypography>
          <IconButton
            onClick={onClose}
            sx={{
              color: theme.palette.customColors.greyTextColor,
              "&:hover": {
                backgroundColor: theme.palette.customColors.lightGreyColor,
                color: theme.palette.customColors.blackBorder,
              },
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>

        <DialogContent
          sx={{
            padding: { xs: "20px", sm: "24px" },
            flex: 1,
            overflow: "auto",
            maxHeight: "60vh",
            "&::-webkit-scrollbar": {
              width: "8px",
            },
            "&::-webkit-scrollbar-track": {
              backgroundColor: theme.palette.customColors.greyBackground,
              borderRadius: "4px",
            },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: theme.palette.customColors.greyDarkborder,
              borderRadius: "4px",
              "&:hover": {
                backgroundColor: theme.palette.customColors.greyTextColor,
              },
            },
          }}
        >
          {packs.length === 0 ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                minHeight: "200px",
              }}
            >
              <CustomTypography
                variant="body2"
                style={{ color: theme.palette.customColors.greyTextColor }}
              >
                No packs available
              </CustomTypography>
            </Box>
          ) : (
            <TableContainer
              component={Paper}
              sx={{
                borderRadius: "12px",
                boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
                overflow: "hidden",
              }}
            >
              <Table>
                <TableHead>
                  <TableRow
                    sx={{
                      backgroundColor:
                        theme.palette.customColors.lightGreyColor,
                    }}
                  >
                    <TableCell
                      sx={{
                        fontWeight: 600,
                        color: theme.palette.customColors.blackBorder,
                      }}
                    >
                      No
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: 600,
                        color: theme.palette.customColors.blackBorder,
                      }}
                    >
                      Name
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: 600,
                        color: theme.palette.customColors.blackBorder,
                      }}
                    >
                      Size
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: 600,
                        color: theme.palette.customColors.blackBorder,
                      }}
                    >
                      Color
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: 600,
                        color: theme.palette.customColors.blackBorder,
                      }}
                    >
                      Quantity
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {packs.map((pack, index) => (
                    <TableRow key={index} hover>
                      <TableCell>{index + 1}</TableCell>
                      <TableCell>
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 500,
                            color:
                              pack.name !== "-"
                                ? theme.palette.customColors.blackBorder
                                : theme.palette.customColors.greyTextColor,
                            fontStyle: pack.name !== "-" ? "normal" : "italic",
                          }}
                        >
                          {pack.name}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 500,
                            color:
                              pack.size !== "-"
                                ? theme.palette.customColors.blackBorder
                                : theme.palette.customColors.greyTextColor,
                            fontStyle: pack.size !== "-" ? "normal" : "italic",
                          }}
                        >
                          {pack.size}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 500,
                            color:
                              pack.color !== "-" &&
                              pack.color.toUpperCase() !== "NULL"
                                ? theme.palette.customColors.blackBorder
                                : theme.palette.customColors.greyTextColor,
                            fontStyle:
                              pack.color !== "-" &&
                              pack.color.toUpperCase() !== "NULL"
                                ? "normal"
                                : "italic",
                          }}
                        >
                          {pack.color.toUpperCase() === "NULL"
                            ? "-"
                            : pack.color}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 500,
                            color:
                              pack.quantity !== "-"
                                ? theme.palette.customColors.blackBorder
                                : theme.palette.customColors.greyTextColor,
                            fontStyle:
                              pack.quantity !== "-" ? "normal" : "italic",
                          }}
                        >
                          {pack.quantity}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>

        {/* Footer Actions */}
        <DialogActions
          sx={{
            padding: { xs: "16px 20px 20px", sm: "20px 24px 24px" },
            borderTop: `1px solid ${theme.palette.customColors.greyBorder}`,
            backgroundColor: theme.palette.customColors.lightGreyColor,
            justifyContent: "flex-end",
          }}
        >
          <Button
            onClick={onClose}
            variant="contained"
            sx={{
              backgroundColor: theme.palette.customColors.blueGradientStart,
              color: theme.palette.customColors.white,
              fontWeight: 600,
              padding: "10px 24px",
              borderRadius: "8px",
              "&:hover": {
                backgroundColor:
                  theme.palette.customColors.blueGradientHoverStart,
                transform: "translateY(-1px)",
                boxShadow: `0 4px 12px ${theme.palette.customColors.blueGradientStart}30`,
              },
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Box>
    </Dialog>
  );
};

export default ViewPackProductsPopup;
